import styles from "./ds.module.css";

/*
    The two small chart shapes a dashboard widget uses, as inline SVG and divs on the tokens:
    a pie with a legend, and horizontal bars. Colours are rationed: brand purple, then indigo,
    then the semantic tints, so a six-slice pie still reads as one system.
*/

export interface ChartDatum { label: string; value: number; display: string; color?: string }

export const CHART_COLORS = [
    "var(--moti-primary)", "var(--indigo-600)", "var(--ok-500)", "var(--attention-500)", "var(--info-fg)", "var(--slate-400)",
    "var(--pause-500)", "var(--danger-600)", "var(--indigo-900)", "var(--slate-600)",
];

export const chartColor = (i: number) => CHART_COLORS[i % CHART_COLORS.length];

export function PieChart({ series }: { series: ChartDatum[] }) {
    const total = series.reduce((a, x) => a + x.value, 0) || 1;
    const R = 62, c = 66;
    let a0 = -Math.PI / 2;
    const slices = series.map((x, i) => {
        const f = x.value / total;
        const a1 = a0 + f * 2 * Math.PI;
        const d = f >= 0.9999
            ? `M ${c} ${c - R} A ${R} ${R} 0 1 1 ${c - 0.01} ${c - R} Z`
            : `M ${c} ${c} L ${(c + R * Math.cos(a0)).toFixed(2)} ${(c + R * Math.sin(a0)).toFixed(2)} A ${R} ${R} 0 ${f > 0.5 ? 1 : 0} 1 ${(c + R * Math.cos(a1)).toFixed(2)} ${(c + R * Math.sin(a1)).toFixed(2)} Z`;
        a0 = a1;
        return { ...x, d, pct: `${(f * 100).toFixed(0)}%`, color: x.color ?? chartColor(i) };
    });
    return (
        <div className={styles.pie}>
            <svg width="116" height="116" viewBox="0 0 132 132" role="img" aria-label="Distribution">
                {slices.map((s, i) => <path key={i} d={s.d} style={{ fill: s.color, stroke: "#fff", strokeWidth: 1.5 }}><title>{`${s.label}: ${s.display} (${s.pct})`}</title></path>)}
            </svg>
            <div className={styles.legend}>
                {slices.map((s, i) => (
                    <div key={i} className={styles.legendRow}>
                        <span className={styles.legendSwatch} style={{ background: s.color }} />
                        <span className={styles.legendLabel} title={s.label}>{s.label}</span>
                        <span className={styles.legendValue}>{s.display}</span>
                        <span className={styles.legendPct}>{s.pct}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function BarChart({ series }: { series: ChartDatum[] }) {
    const max = Math.max(0, ...series.map((x) => x.value)) || 1;
    return (
        <div className={styles.bars}>
            {series.map((x, i) => (
                <div key={i} className={styles.bar}>
                    <span className={styles.barLabel} title={x.label}>{x.label}</span>
                    <div className={styles.barTrack}><div className={styles.barFill} style={{ width: `${((x.value / max) * 100).toFixed(1)}%`, background: x.color ?? chartColor(i) }} /></div>
                    <span className={styles.barValue}>{x.display}</span>
                </div>
            ))}
        </div>
    );
}
